import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Button, ButtonBase, IconButton, Stack, Typography } from '@mui/material';
import { InfoOutlined, PlayArrow, VolumeOff, VolumeUp } from '@mui/icons-material';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { formatDuration } from 'components/play/workoutEstimate';
import TrimmedVideo from 'components/play/recovery/TrimmedVideo';

const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

// Memoria por PRÁCTICA y por pestaña: el saludo se ve una vez, no en cada intento.
const SEEN_PREFIX = 'otx_play_intro_seen_';

// `onProgress` avisa una vez por segundo. Un salto mayor a esto no es reproducción real
// (seek, recuperación tras un buffer largo) y no se acredita como tiempo visto.
const MAX_TICK_MS = 2000;

// Sin avance de video en este lapso el archivo está trabado (red caída, códec que no arranca
// y no dispara `error`): se abre la puerta igual. Nadie se queda sin poder entrenar.
const STALL_MS = 8000;

const frameSx = {
  position: 'relative',
  width: '100%',
  aspectRatio: '16 / 9',
  borderRadius: '16px',
  overflow: 'hidden',
  border: '1px solid',
  borderColor: 'hairline.main',
  backgroundColor: 'surface.raised'
};

const videoSx = { width: '100%', height: '100%' };

// Overlay de rescate cuando el navegador rechaza el autoplay: ocupa todo el video para que
// el toque sea imposible de fallar con el pulgar.
const playOverlaySx = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  backgroundColor: 'surface.raised',
  color: 'text.primary',
  transition: `transform 200ms ${EASE}`,
  '&:active': { transform: 'scale(0.98)' },
  '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'gold.main', outlineOffset: -2 }
};

const soundButtonSx = {
  position: 'absolute',
  right: 10,
  bottom: 10,
  color: 'text.primary',
  backgroundColor: 'surface.raised',
  border: '1px solid',
  borderColor: 'hairline.strong',
  transition: `transform 200ms ${EASE}`,
  '&:hover': { backgroundColor: 'surface.raised' },
  '&:active': { transform: 'scale(0.98)' }
};

const cancelButtonSx = {
  alignSelf: 'center',
  color: 'text.secondary',
  fontWeight: 700,
  textTransform: 'none',
  transition: `color 200ms ${EASE}`,
  '&:hover': { backgroundColor: 'transparent', color: 'text.primary' },
  '&:active': { transform: 'scale(0.98)' }
};

// En Safari privado `sessionStorage` LANZA al leer o escribir: sin el try/catch el intro
// entero se cae y el cliente no puede entrar a la práctica.
const readSeen = (key) => {
  if (!key) return false;
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const writeSeen = (key) => {
  if (!key) return;
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    // Sin memoria disponible se vuelve a mostrar el saludo: molesto, nunca bloqueante.
  }
};

/**
 * OTRIX Play — video de BIENVENIDA de una práctica de recuperación con puerta de
 * "listo para empezar".
 *
 * La entrenadora graba un saludo que explica qué se va a hacer; el CTA de arranque se
 * habilita cuando el cliente vio lo esencial (`readyMs` de tiempo REPRODUCIDO). La puerta
 * es blanda a propósito: error de carga, autoplay bloqueado, video trabado, video más corto
 * que `readyMs` o práctica sin `required` desbloquean el botón al instante.
 */
function PlayRecoveryIntro({
  videoUrl,
  startMs = null,
  endMs = null,
  readyMs = null,
  hasAudio = false,
  required = false,
  practiceId = null,
  onStart,
  onCancel
}) {
  const { t } = useTranslation(['play']);

  const seenKey = practiceId ? `${SEEN_PREFIX}${practiceId}` : null;
  const gateMs = required && readyMs > 0 ? readyMs : 0;

  // El estado inicial se calcula UNA vez: si no hay puerta o el saludo ya se vio en esta
  // pestaña, el botón nace vivo y el video queda como acompañamiento.
  const [unlocked, setUnlocked] = useState(() => gateMs === 0 || readSeen(seenKey));
  const [gated] = useState(() => gateMs > 0 && !readSeen(seenKey));

  const [watchedMs, setWatchedMs] = useState(0);
  const [muted, setMuted] = useState(true);
  const [blocked, setBlocked] = useState(false);
  const [failed, setFailed] = useState(false);
  const [stalled, setStalled] = useState(false);
  // Remontar el <video> es la única forma de reintentar el autoplay sin un ref al hijo.
  const [retryKey, setRetryKey] = useState(0);

  const lastPosRef = useRef(null);
  const watchedRef = useRef(0);
  const lastAdvanceRef = useRef(Date.now());

  const openGate = useCallback(() => {
    setUnlocked(true);
    writeSeen(seenKey);
  }, [seenKey]);

  // La puerta cuenta TIEMPO VISTO, no tiempo de pared: se acredita el avance real del video
  // entre dos avisos. Si el cliente pausa o el archivo se traba no llegan avisos y el
  // contador se queda quieto; dejar correr un `setInterval` regalaría la bienvenida.
  const handleProgress = useCallback(
    (currentMs) => {
      const pos = Math.max(0, Number(currentMs) || 0);
      const prev = lastPosRef.current;
      lastPosRef.current = pos;
      if (prev === null) return;

      const delta = pos - prev;
      // El origen del tiempo no importa (absoluto del archivo o relativo al recorte): solo
      // se suman avances contiguos hacia adelante.
      if (delta <= 0 || delta > MAX_TICK_MS) return;

      watchedRef.current += delta;
      lastAdvanceRef.current = Date.now();
      setWatchedMs(watchedRef.current);
    },
    []
  );

  // Puerta cumplida.
  useEffect(() => {
    if (!unlocked && gateMs > 0 && watchedMs >= gateMs) openGate();
  }, [unlocked, gateMs, watchedMs, openGate]);

  // Vigía de video trabado: si el tiempo visto no avanza, se abre la puerta igual.
  useEffect(() => {
    if (unlocked) return undefined;
    const timer = setInterval(() => {
      if (Date.now() - lastAdvanceRef.current >= STALL_MS) {
        setStalled(true);
        setUnlocked(true);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [unlocked, watchedMs]);

  const handleError = useCallback(() => {
    // El video no carga: desbloqueo inmediato. La práctica no depende del saludo.
    setFailed(true);
    setBlocked(false);
    setUnlocked(true);
  }, []);

  const handlePlayBlocked = useCallback(() => {
    setBlocked(true);
  }, []);

  const handleEnded = useCallback(() => {
    // El saludo terminó antes de `readyMs` (video más corto que la marca): ya no hay nada
    // más que ver, sería una trampa dejar el botón apagado.
    openGate();
  }, [openGate]);

  const handleRetry = useCallback(() => {
    setBlocked(false);
    lastPosRef.current = null;
    lastAdvanceRef.current = Date.now();
    setRetryKey((key) => key + 1);
  }, []);

  const handleToggleSound = useCallback(() => {
    setMuted((value) => !value);
  }, []);

  const handleStart = useCallback(() => {
    writeSeen(seenKey);
    onStart();
  }, [seenKey, onStart]);

  const remainingSeconds = Math.max(0, Math.ceil((gateMs - watchedMs) / 1000));
  const progress = gateMs > 0 ? Math.min(1, watchedMs / gateMs) : 1;
  const notice = failed || stalled;

  return (
    <Stack spacing={2.5} sx={{ width: '100%' }}>
      {/* El video manda: primero se ve a la entrenadora, después se lee. */}
      <Box sx={frameSx}>
        <TrimmedVideo
          key={retryKey}
          src={videoUrl}
          startMs={startMs}
          endMs={endMs}
          // Arranca SIEMPRE mudo: los navegadores solo autorizan el autoplay sin sonido.
          muted={muted}
          loop={false}
          autoPlay
          controls={false}
          objectFit="cover"
          onEnded={handleEnded}
          onError={handleError}
          onPlayBlocked={handlePlayBlocked}
          onProgress={handleProgress}
          sx={videoSx}
        />

        {hasAudio && !failed && (
          // Desmutear exige un gesto explícito del usuario, por eso es un botón propio y no
          // un estado automático.
          <IconButton
            size="small"
            onClick={handleToggleSound}
            sx={soundButtonSx}
            aria-label={
              muted
                ? t('recovery.intro.unmute', { ns: 'play', defaultValue: 'Activar sonido' })
                : t('recovery.intro.mute', { ns: 'play', defaultValue: 'Silenciar' })
            }>
            {muted ? <VolumeOff fontSize="small" /> : <VolumeUp fontSize="small" />}
          </IconButton>
        )}

        {blocked && !failed && (
          <ButtonBase
            onClick={handleRetry}
            sx={playOverlaySx}
            aria-label={t('recovery.intro.tapToPlay', {
              ns: 'play',
              defaultValue: 'Toca para reproducir la bienvenida'
            })}>
            <PlayArrow sx={{ fontSize: 44, color: 'gold.main' }} />
            <Typography variant="caption" fontWeight={700} sx={{ color: 'text.secondary' }}>
              {t('recovery.intro.tapToPlay', {
                ns: 'play',
                defaultValue: 'Toca para reproducir la bienvenida'
              })}
            </Typography>
          </ButtonBase>
        )}
      </Box>

      <Stack spacing={0.5} sx={{ textAlign: 'center' }}>
        <Typography variant="h6" fontWeight={800} letterSpacing="-0.01em">
          {t('recovery.intro.title', { ns: 'play', defaultValue: 'Antes de empezar' })}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('recovery.intro.body', {
            ns: 'play',
            defaultValue: 'Tu entrenadora te cuenta de qué se trata esta práctica.'
          })}
        </Typography>
      </Stack>

      {notice && (
        // Aviso discreto, sin detalle técnico: el cliente solo necesita saber que puede seguir.
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" role="status">
          <InfoOutlined sx={{ fontSize: 16, color: 'sessionStatus.absent.fg' }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {failed
              ? t('recovery.intro.videoError', {
                  ns: 'play',
                  defaultValue: 'No pudimos cargar la bienvenida. Puedes empezar la práctica.'
                })
              : t('recovery.intro.videoStalled', {
                  ns: 'play',
                  defaultValue: 'La bienvenida no avanza. Puedes empezar la práctica.'
                })}
          </Typography>
        </Stack>
      )}

      <Stack spacing={1}>
        <Button
          fullWidth
          onClick={handleStart}
          disabled={!unlocked}
          aria-describedby={gated ? 'play-intro-gate-hint' : undefined}
          sx={premiumButtonSx}>
          {unlocked
            ? t('recovery.intro.start', { ns: 'play', defaultValue: 'Empezar práctica' })
            : t('recovery.intro.startsIn', {
                ns: 'play',
                time: formatDuration(remainingSeconds),
                defaultValue: 'Iniciar en {{time}}'
              })}
        </Button>

        {gated && (
          <>
            {/* Solo `transform`: la barra no repinta layout en cada tick. */}
            <Box sx={{ height: 4, borderRadius: 999, backgroundColor: 'surface.overlay', overflow: 'hidden' }}>
              <Box
                sx={{
                  height: '100%',
                  width: '100%',
                  borderRadius: 999,
                  backgroundColor: 'gold.main',
                  transform: `scaleX(${progress})`,
                  transformOrigin: 'left',
                  transition: 'transform 1s linear'
                }}
              />
            </Box>
            <Typography
              id="play-intro-gate-hint"
              variant="caption"
              role="status"
              sx={{ color: 'text.disabled', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
              {unlocked
                ? t('recovery.intro.gateOpen', { ns: 'play', defaultValue: 'Listo, ya puedes empezar' })
                : t('recovery.intro.gateHint', {
                    ns: 'play',
                    defaultValue: 'El inicio se habilita al ver la bienvenida'
                  })}
            </Typography>
          </>
        )}
      </Stack>

      {/* Salida siempre disponible: la puerta nunca debe sentirse como un encierro. */}
      <Button onClick={onCancel} sx={cancelButtonSx}>
        {t('recovery.intro.cancel', { ns: 'play', defaultValue: 'Volver' })}
      </Button>
    </Stack>
  );
}

PlayRecoveryIntro.propTypes = {
  /** Video de bienvenida de la práctica. */
  videoUrl: PropTypes.string.isRequired,
  // Esta versión de `require-default-props` todavía exige `defaultProps`, que React 18.3 ya
  // deprecó en componentes de función: el valor por defecto va en la firma.
  // eslint-disable-next-line react/require-default-props
  startMs: PropTypes.number,
  // eslint-disable-next-line react/require-default-props
  endMs: PropTypes.number,
  /** Milisegundos VISTOS a partir de los cuales se habilita el CTA. */
  // eslint-disable-next-line react/require-default-props
  readyMs: PropTypes.number,
  // eslint-disable-next-line react/require-default-props
  hasAudio: PropTypes.bool,
  /** Con `false` el CTA nace habilitado (el saludo es opcional). */
  // eslint-disable-next-line react/require-default-props
  required: PropTypes.bool,
  /** Identifica la práctica en la memoria de `sessionStorage`. */
  // eslint-disable-next-line react/require-default-props
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onStart: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default PlayRecoveryIntro;
